import { useSession } from "@/services/auth";
import { Avatar, Button, Spinner } from "@heroui/react";
import { PageHeader } from "@/components/page-chrome";
import type { PaymentMethod } from "@/api.types";
import {
  usePaymentMethods,
  useMakePaymentMethodDefault,
  useDeletePaymentMethod,
  useMyPaymentTerms,
} from "@/api/use-payment-methods-api";
import {
  usePaymentInstructions,
  useCreditBalance,
} from "@/api/use-payment-options-api";
import {
  Banknote,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  FileText,
  Lock,
  MapPin,
  MessageCircle,
  Pencil,
  Wallet,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { CreateBusinessAccountDialog } from "./create-business-account.dialog";
import { PaymentMethodsError } from "./payment-methods-error";
import { AddPaymentMethodDialog } from "./add-payment-method.dialog";
import { AddCreditDialog } from "./add-credit-dialog";
import { CreditHistoryDialog } from "./credit-history-dialog";
import { EditProfileDialog } from "./edit-profile.dialog";
import {
  PaymentMethodRow,
  type PaymentMethodStatus,
} from "./payment-method-row";
import { VerifyContactDialog } from "./verify-contact-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

function SettingsRow({
  label,
  value,
  trailing,
  last,
}: {
  label: string;
  value: React.ReactNode;
  trailing?: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={`flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 sm:py-3.5 gap-1 sm:gap-3 ${
        last ? "" : "border-b border-divider"
      }`}
    >
      <span className="text-tiny sm:text-sm font-medium text-default-500 sm:text-default-700 uppercase sm:normal-case tracking-wider sm:tracking-normal">
        {label}
      </span>
      <span className="flex flex-wrap items-center gap-2 text-sm text-default-700 sm:text-default-600 min-w-0">
        <span className="break-words sm:truncate min-w-0">{value}</span>
        {trailing}
      </span>
    </div>
  );
}

function VerifiedChip() {
  const { t } = useTranslation("app");

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success-50 text-success-700 text-[10px] font-semibold whitespace-nowrap">
      <CheckCircle2 className="h-2.5 w-2.5" /> {t("settings.verified")}
    </span>
  );
}

function VerifyButton({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation("app");

  return (
    <button
      onClick={onPress}
      className="px-2 py-0.5 rounded-full bg-warning-50 text-warning-700 text-[10px] font-semibold hover:bg-warning-100 whitespace-nowrap"
    >
      {t("settings.verify")}
    </button>
  );
}

export default function SettingsScreen() {
  const { t } = useTranslation("app");
  const { customer, logout } = useSession();
  const {
    data: billingData,
    isLoading: isLoadingPaymentMethods,
    isError: paymentMethodsError,
    refetch: refetchPaymentMethods,
    isFetching: isFetchingPaymentMethods,
  } = usePaymentMethods();
  const { data: paymentInstructions } = usePaymentInstructions();
  const { data: creditBalance } = useCreditBalance();
  const { data: paymentTermsData } = useMyPaymentTerms();

  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isCreateBusinessAccountOpen, setIsCreateBusinessAccountOpen] =
    useState(false);
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const [isAddCreditOpen, setIsAddCreditOpen] = useState(false);
  const [isCreditHistoryOpen, setIsCreditHistoryOpen] = useState(false);
  const [isVerifyEmailOpen, setIsVerifyEmailOpen] = useState(false);
  const [isVerifyPhoneOpen, setIsVerifyPhoneOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (document.body.style.pointerEvents === "none") {
      document.body.style.pointerEvents = "";
    }
  });

  const setDefaultPaymentMethodMutation = useMakePaymentMethodDefault({
    onSuccess: () => {
      toast({ title: t("settings.toast.defaultUpdated") });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("settings.toast.defaultUpdateError"),
      });
    },
  });

  const deletePaymentMethodMutation = useDeletePaymentMethod({
    onSuccess: () => {
      toast({ title: t("settings.toast.paymentRemoved") });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("settings.toast.paymentRemoveError"),
      });
    },
  });

  const getInitial = () => {
    if (customer?.firstName) {
      return customer.firstName.charAt(0).toUpperCase();
    }
    return "";
  };

  const getFullName = () => {
    if (customer) {
      return `${customer.firstName} ${customer.lastName}`;
    }
    return "";
  };

  const getCardBrandLogo = (brand?: string): string => {
    const baseUrl =
      "https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards";
    switch (brand?.toLowerCase()) {
      case "mastercard":
        return `${baseUrl}/mastercard.svg`;
      case "visa":
        return `${baseUrl}/visa.svg`;
      case "amex":
      case "american express":
        return `${baseUrl}/american-express.svg`;
      default:
        return `${baseUrl}/card-generic.svg`;
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);
  };

  const savedCards = billingData?.paymentMethods ?? [];
  const hasSavedCard = savedCards.length > 0;
  const defaultCard = savedCards.find((c) => c.isDefault) ?? savedCards[0];

  const eTransferInstruction = paymentInstructions?.find(
    (i) => i.method === "E_TRANSFER"
  );
  const wechatInstruction = paymentInstructions?.find(
    (i) => i.method === "WECHAT"
  );

  const creditBalanceCents = creditBalance?.balanceCents ?? 0;
  const hasCredit = creditBalanceCents > 0;
  const creditFrozen = creditBalance?.frozen ?? false;

  const hasNetTermsAccess = paymentTermsData?.hasNetTermsAccess ?? false;
  const hasPaymentTerms = (paymentTermsData?.paymentTerms?.length ?? 0) > 0;

  const getNetTermsStatus = (): PaymentMethodStatus => {
    if (!hasNetTermsAccess || !hasPaymentTerms) return "disabled";
    return "enabled";
  };

  const getNetTermsStatusLabel = (): string => {
    if (!hasNetTermsAccess || !hasPaymentTerms)
      return t("settings.netTermsNeedsApproval");
    return t("settings.netTermsEnabled");
  };

  const getNetTermsValue = (): string | undefined => {
    if (!hasNetTermsAccess || !hasPaymentTerms) return undefined;
    const terms = paymentTermsData?.paymentTerms.map((t) => t.name).join(", ");
    return terms;
  };

  return (
    <div className="space-y-9">
      <PageHeader
        eyebrow={t("settings.eyebrow")}
        title={t("settings.title")}
        italic={t("settings.italic")}
        lede={t("settings.lede")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-5 md:gap-6">
        {/* Left Column - Profile & Business */}
        <div className="space-y-5">
          {/* Profile card */}
          <div className="bg-content1 rounded-large shadow-small p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-base font-semibold">
                  {t("settings.basicInfo")}
                </div>
                <div className="text-tiny text-default-500 mt-0.5">
                  {t("settings.basicInfoDescription")}
                </div>
              </div>
              <Avatar
                name={getInitial()}
                size="md"
                classNames={{
                  base: "bg-primary flex-shrink-0",
                  name: "text-primary-foreground font-semibold",
                }}
              />
            </div>

            <div className="flex flex-col">
              <SettingsRow label={t("settings.name")} value={getFullName()} />
              <SettingsRow
                label={t("settings.email")}
                value={customer?.email ?? "—"}
                trailing={
                  customer?.emailVerified ? (
                    <VerifiedChip />
                  ) : (
                    <VerifyButton onPress={() => setIsVerifyEmailOpen(true)} />
                  )
                }
              />
              <SettingsRow
                label={t("settings.phone")}
                value={customer?.phone ?? t("settings.notProvided")}
                last
                trailing={
                  customer?.phone ? (
                    customer?.phoneVerified ? (
                      <VerifiedChip />
                    ) : (
                      <VerifyButton
                        onPress={() => setIsVerifyPhoneOpen(true)}
                      />
                    )
                  ) : null
                }
              />
            </div>

            <Button
              variant="bordered"
              size="sm"
              fullWidth
              className="mt-4"
              onPress={() => setIsEditProfileOpen(true)}
            >
              {t("settings.editProfile")}
            </Button>
          </div>

          {/* Business card */}
          <div className="bg-content1 rounded-large shadow-small p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-base font-semibold">
                  {t("settings.businessAccount")}
                </div>
                <div className="text-tiny text-default-500 mt-0.5">
                  {t("settings.businessDescription")}
                </div>
              </div>
              {customer?.businessAccount && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-success-50 text-success-700 text-[11px] font-semibold">
                  <CheckCircle2 className="h-2.5 w-2.5" />{" "}
                  {t("settings.active")}
                </span>
              )}
            </div>

            {customer?.businessAccount ? (
              <>
                <div className="flex flex-col">
                  <SettingsRow
                    label={t("settings.companyName")}
                    value={customer.businessAccount.companyName}
                  />
                  {customer.businessAccount.taxNumber && (
                    <SettingsRow
                      label={t("settings.taxNumber")}
                      value={customer.businessAccount.taxNumber}
                    />
                  )}
                  {customer.businessAccount.registrationNo && (
                    <SettingsRow
                      label={t("settings.registration")}
                      value={customer.businessAccount.registrationNo}
                      last
                    />
                  )}
                </div>
                <Button
                  variant="bordered"
                  size="sm"
                  fullWidth
                  className="mt-4"
                  onPress={() => setIsCreateBusinessAccountOpen(true)}
                >
                  {t("settings.manageBusiness")}
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-default-500">
                  {t("settings.businessEmpty")}
                </p>
                <Button
                  color="primary"
                  fullWidth
                  className="mt-4"
                  onPress={() => setIsCreateBusinessAccountOpen(true)}
                >
                  {t("settings.addBusiness")}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Right Column - Payment methods + Billing + Danger */}
        <div className="space-y-5">
          <div className="bg-content1 rounded-large shadow-small overflow-hidden">
            <div className="px-6 py-5 border-b border-divider flex items-center justify-between gap-4">
              <div>
                <div className="text-base font-semibold">
                  {t("settings.paymentMethods")}
                </div>
                <div className="text-tiny text-default-500 mt-0.5">
                  {t("settings.paymentMethodsDescription")}
                </div>
              </div>
              <Button
                size="sm"
                color="primary"
                onPress={() => setIsAddPaymentMethodOpen(true)}
              >
                {t("settings.addMethod")}
              </Button>
            </div>

            <div>
              {isLoadingPaymentMethods ? (
                <div className="flex justify-center py-8">
                  <Spinner size="md" />
                </div>
              ) : (
                <div>
                  {/* Credit/Debit Card */}
                  {!customer?.isTestAccount && paymentMethodsError && (
                    <PaymentMethodsError
                      onRetry={() => void refetchPaymentMethods()}
                      isRetrying={isFetchingPaymentMethods}
                    />
                  )}
                  {!customer?.isTestAccount && !paymentMethodsError && (
                    <PaymentMethodRow
                      icon={<CreditCard className="h-5 w-5" />}
                      title={t("settings.cardTitle")}
                      description={
                        hasSavedCard
                          ? t("settings.cardSavedDescription")
                          : t("settings.cardEmptyDescription")
                      }
                      status={hasSavedCard ? "enabled" : "setup_required"}
                      value={
                        defaultCard ? (
                          <>
                            <img
                              src={getCardBrandLogo(defaultCard.brand)}
                              alt={defaultCard.brand ?? t("settings.cardAlt")}
                              className="h-4 w-auto"
                            />
                            <span>•••• {defaultCard.last4}</span>
                          </>
                        ) : undefined
                      }
                      action={{
                        label: hasSavedCard
                          ? t("settings.manage")
                          : t("settings.addCard"),
                        onClick: () => setIsAddPaymentMethodOpen(true),
                      }}
                      details={
                        hasSavedCard ? (
                          <div className="space-y-3 pt-2">
                            {savedCards
                              .sort((a, b) =>
                                a.isDefault ? -1 : b.isDefault ? 1 : 0
                              )
                              .map((method: PaymentMethod) => (
                                <div
                                  key={method.id}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 bg-background rounded-lg border border-default-200"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-10 h-7 flex items-center justify-center">
                                      <img
                                        src={getCardBrandLogo(method.brand)}
                                        alt={
                                          method.brand ?? t("settings.cardAlt")
                                        }
                                        className="h-5 w-auto"
                                      />
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-medium whitespace-nowrap">
                                        •••• •••• •••• {method.last4}
                                      </span>
                                      {method.isDefault && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-default-100 text-default-700 text-[10px] font-semibold">
                                          {t("settings.default")}
                                        </span>
                                      )}
                                      <span className="text-xs text-default-500 whitespace-nowrap">
                                        {t("settings.expiresShort", {
                                          month: method.expiryMonth,
                                          year: method.expiryYear,
                                        })}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 sm:gap-2 ml-auto">
                                    {!method.isDefault && (
                                      <Button
                                        variant="light"
                                        size="sm"
                                        onPress={() =>
                                          setDefaultPaymentMethodMutation.mutate(
                                            method.id
                                          )
                                        }
                                      >
                                        {t("settings.setDefault")}
                                      </Button>
                                    )}
                                    <Button
                                      variant="light"
                                      size="sm"
                                      color="danger"
                                      onPress={() =>
                                        deletePaymentMethodMutation.mutate(
                                          method.id
                                        )
                                      }
                                    >
                                      {t("settings.remove")}
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            <Button
                              variant="flat"
                              size="sm"
                              fullWidth
                              className="mt-2"
                              startContent={<CreditCard className="h-4 w-4" />}
                              onPress={() => setIsAddPaymentMethodOpen(true)}
                            >
                              {t("settings.addNewCard")}
                            </Button>
                          </div>
                        ) : undefined
                      }
                    />
                  )}

                  {/* Account credit */}
                  <PaymentMethodRow
                    icon={<Wallet className="h-5 w-5" />}
                    title={t("settings.accountCredit")}
                    description={t("settings.accountCreditDescription")}
                    status={
                      creditFrozen
                        ? "locked"
                        : hasCredit
                          ? "enabled"
                          : "disabled"
                    }
                    statusLabel={
                      creditFrozen
                        ? t("settings.frozen")
                        : hasCredit
                          ? t("settings.available")
                          : t("settings.noBalance")
                    }
                    value={formatCurrency(creditBalanceCents)}
                    action={
                      savedCards.length > 0
                        ? {
                            label: t("settings.addFunds"),
                            onClick: () => setIsAddCreditOpen(true),
                          }
                        : undefined
                    }
                    details={
                      <div className="space-y-3">
                        {creditFrozen && (
                          <p className="text-sm text-danger">
                            {t("settings.creditFrozenBody")}
                          </p>
                        )}
                        <Button
                          size="sm"
                          variant="flat"
                          onPress={() => setIsCreditHistoryOpen(true)}
                        >
                          {t("settings.viewHistory")}
                        </Button>
                      </div>
                    }
                  />

                  {/* E-Transfer */}
                  {!customer?.isTestAccount && (
                    <PaymentMethodRow
                      icon={<Banknote className="h-5 w-5" />}
                      title={t("settings.eTransfer")}
                      description={t("settings.eTransferDescription")}
                      status={
                        eTransferInstruction?.isActive
                          ? "enabled"
                          : "coming_soon"
                      }
                      details={
                        eTransferInstruction?.isActive ? (
                          <div className="space-y-2 text-sm">
                            {eTransferInstruction.eTransferEmail && (
                              <p>
                                <span className="text-default-500">
                                  {t("settings.sendETransferTo")}{" "}
                                </span>
                                <span className="font-medium">
                                  {eTransferInstruction.eTransferEmail}
                                </span>
                              </p>
                            )}
                            {eTransferInstruction.instructions && (
                              <p className="text-default-500">
                                {eTransferInstruction.instructions}
                              </p>
                            )}
                          </div>
                        ) : undefined
                      }
                    />
                  )}

                  {/* WeChat Pay */}
                  {!customer?.isTestAccount && (
                    <PaymentMethodRow
                      icon={<MessageCircle className="h-5 w-5" />}
                      title={t("settings.wechatPay")}
                      description={t("settings.wechatDescription")}
                      status={
                        wechatInstruction?.isActive ? "enabled" : "coming_soon"
                      }
                      details={
                        wechatInstruction?.isActive &&
                        wechatInstruction.wechatQRCode ? (
                          <div className="flex justify-center py-2">
                            <img
                              src={wechatInstruction.wechatQRCode}
                              alt={t("settings.wechatQrAlt")}
                              className="h-32 w-32"
                            />
                          </div>
                        ) : undefined
                      }
                    />
                  )}

                  {/* NET Terms */}
                  {!customer?.isTestAccount && (
                    <PaymentMethodRow
                      icon={<FileText className="h-5 w-5" />}
                      title={t("settings.netTerms")}
                      description={
                        !hasNetTermsAccess || !hasPaymentTerms
                          ? t("settings.netTermsApplyDescription")
                          : t("settings.netTermsApprovedDescription")
                      }
                      status={getNetTermsStatus()}
                      statusLabel={getNetTermsStatusLabel()}
                      value={getNetTermsValue()}
                      details={
                        !hasNetTermsAccess || !hasPaymentTerms ? (
                          <div className="space-y-3">
                            <p className="text-sm text-default-500">
                              {t("settings.netTermsBody")}
                            </p>
                            <Button
                              as="a"
                              href="mailto:support@shippingtooez.com?subject=NET%20Terms%20Application"
                              variant="flat"
                              size="sm"
                              endContent={<ExternalLink className="h-4 w-4" />}
                            >
                              {t("settings.contactSales")}
                            </Button>
                          </div>
                        ) : hasNetTermsAccess && hasPaymentTerms ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-default-500">
                                  {t("settings.creditLimit")}
                                </p>
                                <p className="font-medium">
                                  {formatCurrency(
                                    paymentTermsData?.creditLimit ?? 0
                                  )}
                                </p>
                              </div>
                              <div>
                                <p className="text-default-500">
                                  {t("settings.availableCredit")}
                                </p>
                                <p className="font-medium">
                                  {formatCurrency(
                                    paymentTermsData?.availableCredit ?? 0
                                  )}
                                </p>
                              </div>
                            </div>
                            {paymentTermsData?.hasUnpaidInvoices && (
                              <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
                                <p className="text-sm text-foreground">
                                  {t("settings.unpaidInvoices", {
                                    count: paymentTermsData.unpaidInvoiceCount,
                                  })}
                                </p>
                              </div>
                            )}
                          </div>
                        ) : undefined
                      }
                    />
                  )}
                </div>
              )}

              {/* Subtle Stripe footer */}
              <div className="px-6 py-3 border-t border-divider flex items-center justify-center gap-2 text-tiny text-default-500">
                <Lock className="h-3 w-3" />
                {t("settings.stripeFooter")}
              </div>
            </div>
          </div>

          {/* Billing address card */}
          {customer?.billing?.address && (
            <div className="bg-content1 rounded-large shadow-small p-6">
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-medium bg-primary-50 text-primary grid place-items-center flex-shrink-0">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold">
                    {t("settings.billingAddress")}
                  </div>
                  <div className="text-tiny text-default-500 mt-0.5">
                    {t("settings.billingAddressDescription")}
                  </div>
                  <div className="mt-3.5 text-sm text-default-700 leading-relaxed">
                    {customer.billing.address.line1 && (
                      <div className="font-medium">
                        {customer.billing.address.line1}
                      </div>
                    )}
                    {customer.billing.address.line2 && (
                      <div>{customer.billing.address.line2}</div>
                    )}
                    {(customer.billing.address.city ||
                      customer.billing.address.province) && (
                      <div>
                        {[
                          customer.billing.address.city,
                          customer.billing.address.province,
                          customer.billing.address.postalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                    {customer.billing.address.country && (
                      <div>{customer.billing.address.country}</div>
                    )}
                  </div>
                </div>
                <Button
                  variant="bordered"
                  size="sm"
                  startContent={<Pencil className="h-3.5 w-3.5" />}
                  onPress={() => setIsEditProfileOpen(true)}
                >
                  {t("settings.editProfile")}
                </Button>
              </div>
            </div>
          )}

          {/* Danger zone */}
          <div className="bg-content1 rounded-large shadow-small p-6 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">
                {t("settings.dangerZone")}
              </div>
              <div className="text-tiny text-default-500 mt-0.5">
                {t("settings.dangerBody")}
              </div>
            </div>
            <Button
              variant="bordered"
              size="sm"
              className="text-danger-700 ring-1 ring-danger-300"
              onPress={logout}
            >
              {t("settings.signOut")}
            </Button>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {isEditProfileOpen && customer && (
        <EditProfileDialog
          open={isEditProfileOpen}
          onOpenChange={setIsEditProfileOpen}
          customer={customer}
        />
      )}
      {isCreateBusinessAccountOpen && (
        <CreateBusinessAccountDialog
          open={isCreateBusinessAccountOpen}
          onOpenChange={setIsCreateBusinessAccountOpen}
        />
      )}
      {isAddPaymentMethodOpen && (
        <AddPaymentMethodDialog
          open={isAddPaymentMethodOpen}
          onOpenChange={setIsAddPaymentMethodOpen}
        />
      )}
      {isAddCreditOpen && (
        <AddCreditDialog
          open={isAddCreditOpen}
          onOpenChange={setIsAddCreditOpen}
        />
      )}
      {isCreditHistoryOpen && (
        <CreditHistoryDialog
          open={isCreditHistoryOpen}
          onOpenChange={setIsCreditHistoryOpen}
        />
      )}
      {isVerifyEmailOpen && customer?.email && (
        <VerifyContactDialog
          open={isVerifyEmailOpen}
          onOpenChange={setIsVerifyEmailOpen}
          type="email"
          contact={customer.email}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: ["customer"] });
          }}
        />
      )}
      {isVerifyPhoneOpen && customer?.phone && (
        <VerifyContactDialog
          open={isVerifyPhoneOpen}
          onOpenChange={setIsVerifyPhoneOpen}
          type="phone"
          contact={customer.phone}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: ["customer"] });
          }}
        />
      )}
    </div>
  );
}
